/**
 * BlueBean API連携(2026-09-17。docs/specs/bluebean-api.md §12)。admin のみ。
 *  - 業務ID: 会社マスタの BB業務コードごとに BlueBean の業務IDを入れる(API は業務IDで呼ぶ)
 *  - 取得: サーバー(Cloud Functions)に1日分の取得を頼む。毎朝6時の自動取得(前日分)は設定でオン/オフ
 *  - 内部IDの対応: API のオペレーターは BlueBean の内部ID。候補を確かめてマスタに登録する(P-36)
 *  - 比較: 会社別の架電数・通話時間と、取得件数を CSV 取込の結果と並べる
 * 画面から BlueBean には接続しない(連携ユーザのパスワードを画面に出さないため)。
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useCurrentUser } from '@core/auth/AuthProvider';
import DateField from '@core/components/DateField';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import { addDays, fmtDuration } from '@core/dashboard/calc';
import { normalizeCode, resolveCompanyByCode } from '@core/import/activity';
import { BB_API_DEFAULTS, runBbApiFinalize, fetchBbApiDaily, fetchBbApiSettings, fetchRecentBbApiDaily, requestBbApiFetch, saveBbApiSettings, type BbApiSettings } from '@core/import/bbApiRepo';
import { BB_ID_SOURCE_LABELS, compareWithCsv, suggestBbIds, type AccountRef, type ApiCheck, type BbApiDaily } from '@core/import/bluebeanApiDaily';
import { fetchBbDailyStats } from '@core/import/importRepo';
import type { BbDailyStat } from '@core/import/types';
import { fmtTs } from '@core/lib/auditLog';
import { todayStr } from '@core/lib/date';
import type { Company, DateStr, Operator } from '@core/lib/types';
import { fetchCompanies } from './companyRepo';
import { fetchOperators, setBbInternalIds } from './operatorRepo';

const cellInput = { padding: '6px 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit' } as const;
const STATUS_LABELS: Record<BbApiDaily['status'], { text: string; cls: string }> = {
  ok: { text: '取得済み', cls: 'b-ok' },
  partial: { text: '一部失敗', cls: 'b-pend' },
  error: { text: '失敗', cls: 'b-ng' },
};
const slashDate = (d: DateStr) => d.replaceAll('-', '/');
const API_CHECK_LABELS: Record<ApiCheck['call_history'], { text: string; cls: string }> = {
  available: { text: '使えます', cls: 'b-ok' },
  not_found: { text: 'この版では使えない見込み', cls: 'b-ng' },
  auth_error: { text: 'ID・パスワードが通りません', cls: 'b-ng' },
  error: { text: '確認できませんでした', cls: 'b-pend' },
};
/** 通話明細の「オペレータ」列が 内部ID(数字)か。「氏名 (SN1109)」形式なら BB ID を取り出す */
const isInternalId = (v: string) => /^\d+$/.test(v);
const bbIdInCell = (v: string) => v.match(/[(（]\s*([A-Za-z]{2,4}\d{2,4})\s*[)）]/)?.[1]?.toUpperCase() ?? null;

/** 時刻の選択肢(JST) */
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function BbApiPage() {
  const user = useCurrentUser();
  const toast = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [saved, setSaved] = useState<BbApiSettings>({ campaigns: {}, schedule_enabled: false, live_enabled: false, ...BB_API_DEFAULTS });
  const [campaignText, setCampaignText] = useState<Record<string, string>>({});
  const [schedule, setSchedule] = useState(false);
  const [live, setLive] = useState(false);
  /** 速報の間隔(分)・時間帯・締めの時刻(2026-09-20 発注者指示。D-106) */
  const [everyMin, setEveryMin] = useState<number>(BB_API_DEFAULTS.live_every_min);
  const [fromHour, setFromHour] = useState<number>(BB_API_DEFAULTS.live_from_hour);
  const [toHour, setToHour] = useState<number>(BB_API_DEFAULTS.live_to_hour);
  const [finalizeHour, setFinalizeHour] = useState<number>(BB_API_DEFAULTS.finalize_hour);
  const [finalizing, setFinalizing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [date, setDate] = useState<DateStr>(addDays(todayStr(), -1));
  const [daily, setDaily] = useState<BbApiDaily | null>(null);
  const [stats, setStats] = useState<Map<string, BbDailyStat>>(new Map());
  const [recent, setRecent] = useState<BbApiDaily[]>([]);
  const [fetching, setFetching] = useState(false);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const savedText = useMemo(() => Object.fromEntries(Object.entries(saved.campaigns).map(([k, v]) => [k, String(v)])), [saved]);
  const dirty =
    schedule !== saved.schedule_enabled ||
    live !== saved.live_enabled ||
    everyMin !== saved.live_every_min ||
    fromHour !== saved.live_from_hour ||
    toHour !== saved.live_to_hour ||
    finalizeHour !== saved.finalize_hour || JSON.stringify(Object.fromEntries(Object.entries(campaignText).filter(([, v]) => v.trim() !== ''))) !== JSON.stringify(savedText);
  const guard = useUnsavedGuard(dirty);

  useEffect(() => {
    void Promise.all([fetchCompanies(), fetchOperators(), fetchBbApiSettings(), fetchRecentBbApiDaily().catch(() => [] as BbApiDaily[])])
      .then(([c, o, s, r]) => {
        setCompanies(c);
        setOperators(o);
        setSaved(s);
        setCampaignText(Object.fromEntries(Object.entries(s.campaigns).map(([k, v]) => [k, String(v)])));
        setSchedule(s.schedule_enabled);
        setLive(s.live_enabled);
        setEveryMin(s.live_every_min);
        setFromHour(s.live_from_hour);
        setToHour(s.live_to_hour);
        setFinalizeHour(s.finalize_hour);
        setRecent(r);
      })
      .catch(() => toast('読み込みに失敗しました'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDate = useCallback(
    (d: DateStr) => {
      void Promise.all([fetchBbApiDaily(d), fetchBbDailyStats(d)])
        .then(([doc, st]) => {
          setDaily(doc);
          setStats(st);
          setChecked(new Set());
        })
        .catch(() => toast('取得結果の読み込みに失敗しました'));
    },
    [toast],
  );
  useEffect(() => loadDate(date), [date, loadDate]);

  /**
   * 会社マスタの BB業務コード。コードが未登録の会社は社名(対応履歴の取込と同じく、業務コード＝社名で照合するため)。
   * 設定にだけ残っているコードも出す
   */
  const codeRows = useMemo(() => {
    // 業務IDを入れた BlueBean の業務コードを先に出し、会社は取込と同じ照合(BB業務コード → 社名)で引く。
    // その会社はもう一度出さない(2026-09-18: BlueBean の業務コードと会社マスタのコードが違う会社があるため)
    const rows: { code: string; company: string | null }[] = [];
    const seen = new Set<string>();
    const covered = new Set<string>();
    for (const code of Object.keys(saved.campaigns)) {
      const c = resolveCompanyByCode(code, companies);
      rows.push({ code, company: c?.name ?? null });
      seen.add(code);
      if (c) covered.add(c.id);
    }
    for (const c of companies) {
      if (covered.has(c.id)) continue;
      const codes = (c.bb_business_codes ?? []).filter((x) => normalizeCode(x) !== '');
      for (const raw of codes.length > 0 ? codes : [c.name]) {
        const code = normalizeCode(raw);
        if (!code || seen.has(code)) continue;
        seen.add(code);
        rows.push({ code, company: c.name });
      }
    }
    return rows;
  }, [companies, saved]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    const campaigns: Record<string, number> = {};
    for (const [code, text] of Object.entries(campaignText)) {
      const t = text.trim();
      if (t === '') continue;
      if (!/^\d+$/.test(t) || Number(t) <= 0) {
        toast(`${code} の業務IDは1以上の整数で入力してください`);
        return;
      }
      campaigns[code] = Number(t);
    }
    setBusy(true);
    try {
      const next: BbApiSettings = {
        campaigns,
        schedule_enabled: schedule,
        live_enabled: live,
        live_every_min: everyMin,
        live_from_hour: fromHour,
        live_to_hour: toHour,
        finalize_hour: finalizeHour,
      };
      await saveBbApiSettings(next, user.id);
      setSaved(next);
      toast('保存しました');
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const resultText = (d: DateStr, r: { status: BbApiDaily['status']; messages: string[]; operators: number }) =>
    r.status === 'ok'
      ? `${slashDate(d)} を取得しました`
      : r.status === 'partial'
        ? `${slashDate(d)} を取得しました(一部): ${r.messages[0] ?? ''}`
        : `${slashDate(d)} を取得できませんでした: ${r.messages[0] ?? ''}`;

  async function handleFetch() {
    if (dirty) {
      toast('業務IDの変更を先に保存してください');
      return;
    }
    setFetching(true);
    try {
      const r = await requestBbApiFetch(date);
      toast(resultText(date, r));
      loadDate(date);
      setRecent(await fetchRecentBbApiDaily().catch(() => recent));
    } catch (err) {
      toast(err instanceof Error ? err.message : '取得に失敗しました');
    } finally {
      setFetching(false);
    }
  }

  /** 期間をまとめて取得(1日ずつ順番に。BlueBean の負荷を考えて同時には呼ばない) */
  const [rangeTo, setRangeTo] = useState<DateStr>(addDays(todayStr(), -1));
  const [progress, setProgress] = useState<string | null>(null);
  async function handleFetchRange() {
    if (dirty) {
      toast('業務IDの変更を先に保存してください');
      return;
    }
    const days: DateStr[] = [];
    for (let d = date; d <= rangeTo && days.length < 31; d = addDays(d, 1)) days.push(d);
    if (days.length === 0) {
      toast('「まで」は「から」と同じ日かそれより後にしてください');
      return;
    }
    if (!confirm(`${slashDate(days[0]!)}〜${slashDate(days[days.length - 1]!)} の${days.length}日分を順番に取得します。1日1〜3分かかります。途中でこの画面を閉じないでください`)) return;
    setFetching(true);
    const failed: string[] = [];
    try {
      for (const [i, d] of days.entries()) {
        setProgress(`${slashDate(d)} を取得中…(${i + 1}/${days.length})`);
        try {
          const r = await requestBbApiFetch(d);
          if (r.status === 'error') failed.push(slashDate(d).slice(5));
        } catch (err) {
          failed.push(slashDate(d).slice(5));
          if (err instanceof Error && /まだ動いていません/.test(err.message)) {
            toast(err.message);
            break;
          }
        }
      }
      toast(failed.length === 0 ? `${days.length}日分を取得しました` : `取得できなかった日があります: ${failed.join('、')}`);
      loadDate(date);
      setRecent(await fetchRecentBbApiDaily(40).catch(() => recent));
    } finally {
      setProgress(null);
      setFetching(false);
    }
  }

  // ---- 内部ID → BB ID ----
  const accounts: AccountRef[] = useMemo(
    () => operators.flatMap((o) => o.bb_accounts.map((a) => ({ bb_id: a.bb_id, internal_id: a.bb_internal_id ?? null, person_name: o.name, business: a.business, active: a.active && o.status !== 'retired' }))),
    [operators],
  );
  const nameByBbId = useMemo(() => new Map(operators.flatMap((o) => o.bb_accounts.map((a) => [a.bb_id, o.name] as const))), [operators]);
  const suggestions = useMemo(() => {
    if (!daily) return [];
    const displayNames = [...stats.values()].filter((s) => s.op_report?.display_name).map((s) => ({ bb_id: s.bb_id, name: s.op_report!.display_name }));
    // 取得件数の API で返った内部ID(名前あり)と、通話明細だけに出る内部ID(名前なし。V10.17.3 の古い形式)をまとめて候補を出す
    const cdrOps = daily.cdr_operators ?? {};
    const ids = new Map(Object.entries(daily.operators).map(([internal_id, o]) => [internal_id, o.name]));
    for (const key of Object.keys(cdrOps)) if (isInternalId(key) && !ids.has(key)) ids.set(key, '');
    const opCalls = [...stats.values()].filter((s) => s.op_report).map((s) => ({ bb_id: s.bb_id, talk_count: s.op_report!.talk_count, talk_sec: s.op_report!.talk_sec }));
    return suggestBbIds(
      [...ids.entries()].map(([internal_id, name]) => ({ internal_id, name })),
      accounts,
      displayNames,
      { cdr: cdrOps, op: opCalls },
    ).sort((a, b) => Number(a.source === 'master') - Number(b.source === 'master') || Number(a.internal_id) - Number(b.internal_id));
  }, [daily, stats, accounts]);

  useEffect(() => {
    setPicks(Object.fromEntries(suggestions.map((s) => [s.internal_id, s.bb_id ?? ''])));
  }, [suggestions]);

  const resolved = useMemo(() => new Map(suggestions.map((s) => [s.internal_id, s.bb_id])), [suggestions]);
  const unregistered = suggestions.filter((s) => s.source !== 'master');

  async function handleRegister() {
    const entries = [...checked].map((id) => ({ internal_id: id, bb_id: picks[id] ?? '' })).filter((e) => e.bb_id !== '');
    if (entries.length === 0) {
      toast('登録する行にチェックを入れ、BB IDを選んでください');
      return;
    }
    const dup = entries.map((e) => e.bb_id).filter((b, i, arr) => arr.indexOf(b) !== i);
    if (dup.length > 0) {
      toast(`同じBB ID(${[...new Set(dup)].join('、')})に2つの内部IDを登録しようとしています`);
      return;
    }
    setBusy(true);
    try {
      const n = await setBbInternalIds(entries, operators, user.id);
      toast(`マスタに登録しました(${n}件)`);
      setOperators(await fetchOperators());
      setChecked(new Set());
    } catch (err) {
      toast(err instanceof Error ? err.message : '登録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  // ---- CSV 取込との比較 ----
  const compareRows = useMemo(() => {
    if (!daily) return [];
    const csv = new Map<string, Map<string, number>>();
    for (const s of stats.values()) {
      if (!s.activity) continue;
      csv.set(s.bb_id, new Map(Object.entries(s.activity).map(([code, a]) => [code, a.appointments])));
    }
    return compareWithCsv(daily.operators, (id) => resolved.get(id) ?? null, csv);
  }, [daily, stats, resolved]);
  const companyOfCode = (code: string) => codeRows.find((r) => r.code === code)?.company ?? code;
  const totals = compareRows.reduce(
    (t, r) => ({
      calls: t.calls + (r.api?.calls ?? 0),
      talk: t.talk + (r.api?.talk_sec ?? 0),
      api: t.api + (r.api?.appointments ?? 0),
      csv: t.csv + (r.csv_appointments ?? 0),
    }),
    { calls: 0, talk: 0, api: 0, csv: 0 },
  );
  const accountOptions = useMemo(() => [...accounts].sort((a, b) => a.bb_id.localeCompare(b.bb_id)), [accounts]);

  return (
    <div className="screen">
      {guard.modal}
      <div className="card">
        <h3>業務ID</h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          BlueBean の業務設定にある「業務ID」を、BB業務コードごとに入れてください。対応履歴(取得件数)と通話明細を BlueBean から直接取ります。OPレポートは API が無いため、今までどおり CSV で取り込みます。
        </p>
        {loading ? (
          <div className="empty">読み込み中…</div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="table-scroll">
              <table style={{ maxWidth: 560 }}>
                <thead>
                  <tr>
                    <th>会社</th>
                    <th>BB業務コード</th>
                    <th>業務ID</th>
                  </tr>
                </thead>
                <tbody>
                  {codeRows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="empty">
                        会社マスタに BB業務コードがありません
                      </td>
                    </tr>
                  )}
                  {codeRows.map((r) => (
                    <tr key={r.code}>
                      <td data-label="会社">{r.company ?? <span className="hint">会社マスタに無いコード</span>}</td>
                      <td data-label="BB業務コード">{r.code}</td>
                      <td data-label="業務ID">
                        <input inputMode="numeric" value={campaignText[r.code] ?? ''} onChange={(e) => setCampaignText((p) => ({ ...p, [r.code]: e.target.value }))} style={{ ...cellInput, width: 100 }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13 }}>
              <input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} />
              毎朝6時に前日分を自動で取得する
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13 }}>
              <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
              本日の速報を自動で取得する(前回の続きの分だけ)
            </label>
            {live && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0 0 24px', fontSize: 13 }}>
                <span>間隔</span>
                <select className="ui-input" style={{ width: 90 }} value={everyMin} onChange={(e) => setEveryMin(Number(e.target.value))} aria-label="速報の間隔(分)">
                  {[5, 10, 15, 20, 30, 60].map((m) => (
                    <option key={m} value={m}>
                      {m}分
                    </option>
                  ))}
                </select>
                <span>時間帯</span>
                <select className="ui-input" style={{ width: 80 }} value={fromHour} onChange={(e) => setFromHour(Number(e.target.value))} aria-label="速報の開始時刻">
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}時
                    </option>
                  ))}
                </select>
                <span>〜</span>
                <select className="ui-input" style={{ width: 80 }} value={toHour} onChange={(e) => setToHour(Number(e.target.value))} aria-label="速報の終了時刻">
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}時台まで
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12, fontSize: 13 }}>
              <span>
                <b>締め</b>(その日を1日ぶん取り直して入れ替える)
              </span>
              <select className="ui-input" style={{ width: 90 }} value={finalizeHour} onChange={(e) => setFinalizeHour(Number(e.target.value))} aria-label="締めの時刻">
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h}時
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="ui-btn ui-btn-secondary ui-btn-sm"
                disabled={finalizing}
                onClick={() => {
                  setFinalizing(true);
                  void runBbApiFinalize(date)
                    .then((r) => toast(`${r.date} を締めました(取得 ${r.appointments}件)`))
                    .catch((e: unknown) => toast(e instanceof Error ? e.message : '締めに失敗しました'))
                    .finally(() => setFinalizing(false));
                }}
              >
                {finalizing ? '実行中…' : `上で選んだ日(${date})を今すぐ締める`}
              </button>
            </div>
            <p className="hint" style={{ margin: '6px 0 0' }}>
              速報で取れるのは 取得件数・通話回数・最後の架電時刻 です。実稼働時間・後処理はオペレーターレポートにしか無く、翌日の取込で確定します。
              締めは「稼働が終わったあと・翌日の架電が始まる前」に動かすと、現行シートと同じ数え方になります
            </p>
            <div className="modal-actions">
              <button className="btn-green" type="submit" disabled={busy || !dirty}>
                保存する
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="card">
        <h3>取得</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <DateField value={date} onChange={setDate} />
          <button className="btn-line" type="button" onClick={handleFetch} disabled={fetching || loading}>
            {fetching && !progress ? '取得中…(数分かかることがあります)' : 'この日を取得'}
          </button>
          <span className="hint">または</span>
          <span className="hint">上の日から</span>
          <DateField value={rangeTo} onChange={setRangeTo} />
          <span className="hint">まで</span>
          <button className="btn-line" type="button" onClick={handleFetchRange} disabled={fetching || loading}>
            まとめて取得
          </button>
        </div>
        {progress && <div className="hint" style={{ marginTop: 8, fontWeight: 700 }}>{progress}</div>}
        {recent.length > 0 && (
          <div className="hint" style={{ marginTop: 10 }}>
            取得済みの日:{' '}
            {recent.map((r) => (
              <button key={r.date} type="button" className="txn-link" onClick={() => setDate(r.date)}>
                {slashDate(r.date).slice(5)}({STATUS_LABELS[r.status].text})
              </button>
            ))}
          </div>
        )}
        {daily === null ? (
          <div className="empty">{slashDate(date)} はまだ取得していません</div>
        ) : (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className={`badge ${STATUS_LABELS[daily.status].cls}`}>{STATUS_LABELS[daily.status].text}</span>
              <span>
                {fmtTs(daily.fetched_at)}({daily.fetched_by === 'schedule' ? '自動' : daily.fetched_by})
              </span>
              {daily.api_check && (
                <span>
                  取得件数のAPI <span className={`badge ${API_CHECK_LABELS[daily.api_check.call_history].cls}`}>{API_CHECK_LABELS[daily.api_check.call_history].text}</span>
                </span>
              )}
              <span>業務 {daily.campaigns.length}件</span>
              <span>通話明細 {daily.cdr ? `${daily.cdr.rows.toLocaleString('ja-JP')}行${daily.cdr.format === 'legacy' ? '(古い形式)' : `(突き合わせ ${daily.cdr.matched.toLocaleString('ja-JP')})`}` : '—'}</span>
              <span>オペレーターにつながらなかった通話 {daily.unconnected_calls.toLocaleString('ja-JP')}</span>
            </div>
            {daily.messages.length > 0 && (
              <ul style={{ paddingLeft: 20, marginTop: 8, color: 'var(--amber)' }}>
                {daily.messages.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {daily && Object.keys(daily.cdr_operators ?? {}).length > 0 && (
        <div className="card">
          <h3>通話明細(人ごと)</h3>
          <p className="hint" style={{ marginBottom: 12 }}>
            この日の通話明細を、オペレーターごとに数えたものです(会社には分けていません)。BB ID は、マスタに内部IDを登録した人と、通話明細に BB ID が書かれている人だけ出ます。
          </p>
          <div className="table-scroll">
            <table className="fit-table">
              <thead>
                <tr>
                  <th>BB ID</th>
                  <th>氏名</th>
                  <th>通話明細のオペレータ</th>
                  <th className="num">通話</th>
                  <th className="num">つながった</th>
                  <th className="num">通話時間</th>
                  <th>時間帯</th>
                  <th className="num">OPレポートの通話回数</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(daily.cdr_operators ?? {})
                  .map(([key, s]) => {
                    const bbId = isInternalId(key) ? (accounts.find((a) => a.internal_id === key)?.bb_id ?? null) : bbIdInCell(key);
                    return { key, s, bbId };
                  })
                  .sort((a, b) => (a.bbId ?? '~').localeCompare(b.bbId ?? '~') || b.s.talk_sec - a.s.talk_sec)
                  .map(({ key, s, bbId }) => (
                    <tr key={key}>
                      <td data-label="BB ID">{bbId ?? <span className="hint">未登録</span>}</td>
                      <td data-label="氏名">{bbId ? (nameByBbId.get(bbId) ?? '—') : '—'}</td>
                      <td data-label="通話明細のオペレータ" className="hint">
                        {key}
                      </td>
                      <td data-label="通話" className="num">
                        {s.calls.toLocaleString('ja-JP')}
                      </td>
                      <td data-label="つながった" className="num">
                        {s.talked.toLocaleString('ja-JP')}
                      </td>
                      <td data-label="通話時間" className="num">
                        {fmtDuration(s.talk_sec)}
                      </td>
                      <td data-label="時間帯">{s.first_at ? `${s.first_at.slice(0, 5)}〜${(s.last_at ?? '').slice(0, 5)}` : '—'}</td>
                      <td data-label="OPレポートの通話回数" className="num">
                        {bbId ? (stats.get(bbId)?.op_report?.talk_count ?? '—') : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {daily && suggestions.length > 0 && (
        <div className="card">
          <h3>内部IDとBB IDの対応</h3>
          <p className="hint" style={{ marginBottom: 12 }}>
            API のオペレーターは BlueBean の内部ID で返ります。候補を確かめ、正しい行にチェックを入れてマスタに登録してください(未登録 {unregistered.length}人)。
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>登録</th>
                  <th>内部ID</th>
                  <th>BlueBeanの表示名</th>
                  <th>BB ID</th>
                  <th>根拠</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s) => {
                  const registered = s.source === 'master';
                  return (
                    <tr key={s.internal_id}>
                      <td data-label="登録" style={{ textAlign: 'center' }}>
                        {registered ? (
                          <span className="badge b-ok">登録済み</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={checked.has(s.internal_id)}
                            onChange={(e) =>
                              setChecked((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(s.internal_id);
                                else next.delete(s.internal_id);
                                return next;
                              })
                            }
                          />
                        )}
                      </td>
                      <td data-label="内部ID" className="num">
                        {s.internal_id}
                      </td>
                      <td data-label="表示名">{s.name || '—'}</td>
                      <td data-label="BB ID">
                        {registered ? (
                          <strong>{s.bb_id}</strong>
                        ) : (
                          <select value={picks[s.internal_id] ?? ''} onChange={(e) => setPicks((p) => ({ ...p, [s.internal_id]: e.target.value }))} style={cellInput}>
                            <option value="">(選ぶ)</option>
                            {accountOptions.map((a) => (
                              <option key={a.bb_id} value={a.bb_id}>
                                {a.bb_id} {a.person_name}
                                {a.internal_id ? `(内部ID ${a.internal_id})` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td data-label="根拠">
                        {s.source ? BB_ID_SOURCE_LABELS[s.source] : s.candidates.length > 0 ? `候補 ${s.candidates.join('／')}` : '候補なし'}
                        {s.conflict && <span className="badge b-ng" style={{ marginLeft: 6 }}>重複</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {unregistered.length > 0 && (
            <div className="modal-actions">
              <button className="btn-green" type="button" onClick={handleRegister} disabled={busy || checked.size === 0}>
                チェックした行をマスタに登録({checked.size}件)
              </button>
            </div>
          )}
        </div>
      )}

      {daily && (
        <div className="card">
          <h3>会社別の架電数・通話時間と、CSV取込との比較</h3>
          <p className="hint" style={{ marginBottom: 12 }}>
            取得(CSV)は、この日に取り込んだ顧客情報レポートの件数です。BB ID が決まっていない内部IDの行は、上で対応を登録すると並びます。
          </p>
          <div className="table-scroll">
            <table className="fit-table">
              <thead>
                <tr>
                  <th>BB ID</th>
                  <th>氏名</th>
                  <th>会社</th>
                  <th className="num">架電数</th>
                  <th className="num">完了</th>
                  <th className="num">通話時間</th>
                  <th>時間帯</th>
                  <th className="num">取得(API)</th>
                  <th className="num">取得(CSV)</th>
                  <th className="num">差</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((r) => {
                  const diff = r.api && r.csv_appointments !== null ? r.api.appointments - r.csv_appointments : null;
                  return (
                    <tr key={`${r.bb_id ?? ''}|${r.internal_id ?? ''}|${r.code}`}>
                      <td data-label="BB ID">{r.bb_id ?? <span className="hint">内部ID {r.internal_id}</span>}</td>
                      <td data-label="氏名">{r.bb_id ? (nameByBbId.get(r.bb_id) ?? '—') : r.api_name || '—'}</td>
                      <td data-label="会社">{companyOfCode(r.code)}</td>
                      <td data-label="架電数" className="num">
                        {r.api ? r.api.calls.toLocaleString('ja-JP') : '—'}
                      </td>
                      <td data-label="完了" className="num">
                        {r.api ? r.api.success.toLocaleString('ja-JP') : '—'}
                      </td>
                      <td data-label="通話時間" className="num">
                        {r.api ? fmtDuration(r.api.talk_sec) : '—'}
                      </td>
                      <td data-label="時間帯">{r.api?.first_at ? `${r.api.first_at.slice(0, 5)}〜${(r.api.last_at ?? '').slice(0, 5)}` : '—'}</td>
                      <td data-label="取得(API)" className="num">
                        {r.api ? r.api.appointments : '—'}
                      </td>
                      <td data-label="取得(CSV)" className="num">
                        {r.csv_appointments ?? '—'}
                      </td>
                      <td data-label="差" className={`num ${diff ? 'neg' : ''}`}>
                        {diff === null ? '—' : diff > 0 ? `+${diff}` : diff}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="total">
                  <td colSpan={3}>合計</td>
                  <td className="num">{totals.calls.toLocaleString('ja-JP')}</td>
                  <td className="num"></td>
                  <td className="num">{fmtDuration(totals.talk)}</td>
                  <td></td>
                  <td className="num">{totals.api}</td>
                  <td className="num">{totals.csv}</td>
                  <td className={`num ${totals.api - totals.csv ? 'neg' : ''}`}>{totals.api - totals.csv > 0 ? `+${totals.api - totals.csv}` : totals.api - totals.csv}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
